import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import {
  FormInput,
  FormCheckbox,
  FormRadioButton,
  FormTextArea,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldText,
  getFieldValue,
  getFieldDate,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";
import {
  getFormsySubmissionsByFormIdForClient,
  getIncidents,
} from "../services/soar";

function COMonthlyReport() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const [loadStatus, setLoadStatus] = useState({});
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (initialProgram?.ccx_client_programid && loadStatus.dataSet) {
      setData((d) => {
        const obj = { ...d };

        if (
          obj.SelectedProgram === undefined ||
          getFieldValue(obj.SelectedProgram)
        ) {
          return obj;
        }

        obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
        return obj;
      });

      setFormsyDetails((fd) => {
        const details = { ...fd };

        if (details.ClientProgramId === initialProgram.ccx_client_programid) {
          return details;
        }

        details.ClientProgramId = initialProgram.ccx_client_programid;
        details.ClientProgramName = getFieldName(
          initialProgram.ccx_programunit
        );
        return details;
      });
    }
  }, [initialProgram, loadStatus.dataSet]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }

        setLoadStatus((ls) => {
          return { ...ls, dataSet: true };
        });
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      !safyInfo?.user ||
      loadStatus.configLoaded ||
      loadStatus.loadingConfig
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadStatus((ls) => {
        return { ...ls, loadingConfig: true };
      });
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadStatus((ls) => {
          return {
            ...ls,
            loadingConfig: false,
            configLoaded: true,
            dataSet: true,
          };
        });
      } else {
        setLoadStatus((ls) => {
          return { ...ls, loadingConfig: false, configLoaded: true };
        });
      }
    };

    loadConfig();
  }, [
    formid,
    clientid,
    safyInfo,
    submissionid,
    loadStatus.configLoaded,
    loadStatus.loadingConfig,
  ]);

  const helperConfiguration = useMemo(() => {
    const defaultConfig = {
      buttonBarConfig: {
        sign: {
          display: false,
          initialSignatureType: "full",
        },
        clone: {
          display: false,
        },
        delete: {
          display: false,
        },
        share: {
          display: false,
        },
        comment: {
          display: false,
        },
      },
      signatureType: "full",
      finalSignType: "Provider",
    };
    if (!formsyConfig.HelperConfiguration?.[0]) {
      return defaultConfig;
    }
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      console.log(
        "helperConfiguration JSON:",
        formsyConfig.HelperConfiguration[0]
      );
      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments; // commentsOverride is usually null, but specifically undefined during Final Signing
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
      setCommentToEdit(undefined);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    if (helperConfiguration.finalSignType === "Any") {
      return true;
    }

    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");
    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );
    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [soarIncidentReports, setSoarIncidentReports] = useState([]);
  const [incidentReports, setIncidentReports] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    const loadReports = async () => {
      const signal = controller.signal;
      const responses = await Promise.all([
        getFormsySubmissionsByFormIdForClient(
          "2FF4CF32-24FF-ED11-800F-DC98408D5418", // SAFY TFC Incident Report Form Container Id
          clientid,
          signal
        ),
        getIncidents(clientid, signal),
      ]);

      if (signal.aborted) {
        return;
      }

      if (responses?.length === 2) {
        const formsyIncidents = JSON.parse(responses[0]);

        if (formsyIncidents?.FormSubmissions?.Submission?.length > 0) {
          const activeReports =
            formsyIncidents.FormSubmissions.Submission.filter(
              (ir) => ir.Active === "1" && ir.FinalSigned === "1"
            );
          setIncidentReports(activeReports);
        }

        const soarIncidents = responses[1];
        soarIncidents.sort((a, b) => {
          const aDate = moment(getFieldDate(a.ccx_dateandtimeofincident));
          const bDate = moment(getFieldDate(b.ccx_dateandtimeofincident));

          return aDate.isBefore(bDate) ? 1 : bDate.isBefore(aDate) ? -1 : 0;
        });
        setSoarIncidentReports(soarIncidents);
      }
    };

    loadReports();
    return () => {
      controller.abort();
    };
  }, [clientid]);

  const processFormsyIncidentReports = useCallback(() => {
    setData((d) => {
      const obj = { ...d };
      const startValue = getFieldValue(obj.beginDate);
      const endValue = getFieldValue(obj.endDate);

      if (!startValue || !endValue) {
        return obj;
      }

      const startDate = moment(startValue);
      const endDate = moment(endValue);

      if (endDate.isBefore(startDate)) {
        return obj;
      }

      const monthlyReports = incidentReports.filter((ir) => {
        const dataResult = ir.FormResults.results;
        const incidentDate = moment(getFieldValue(dataResult.DateOfIncident));

        return (
          getFieldValue(dataResult.SelectedProgram) ===
            getFieldValue(obj.SelectedProgram) &&
          incidentDate.isSameOrAfter(startDate) &&
          incidentDate.isSameOrBefore(endDate)
        );
      });

      if (monthlyReports.length === 0) {
        return obj;
      }

      const processedReports = [];

      monthlyReports
        .sort((a, b) => {
          const aDate = moment(
            getFieldValue(a.FormResults.results.DateOfIncident)
          );
          const bDate = moment(
            getFieldValue(b.FormResults.results.DateOfIncident)
          );

          return aDate.isBefore(bDate) ? -1 : bDate.isBefore(aDate) ? 1 : 0;
        })
        .forEach((ir) => {
          const dataResult = ir.FormResults.results;
          const typeOfIncident = getFieldValue(dataResult.TypeOfIncident);
          const dateOfIncident = getFieldValue(dataResult.DateOfIncident);

          let report = `${moment(dateOfIncident).format(
            "l"
          )} - ${typeOfIncident}: `;

          switch (typeOfIncident) {
            case "Abuse/Neglect Allegation against someone in a SAFY Home":
              report += getFieldValue(dataResult.AbuseInHomeDescription);
              break;
            case "Abuse/Neglect Allegation against someone outside of a SAFY Home":
              report += getFieldValue(dataResult.AbuseOutsideHomeDescription);
              break;
            case "AWOL":
              report += getFieldValue(dataResult.AWOLDescription);
              break;
            case "Homicidal Thoughts, Gestures, Threats":
              report += getFieldValue(dataResult.HomicidalThoughtsDescription);
              break;
            case "Manual Restraint by SAFY Home":
              report += getFieldValue(dataResult.RestraintDescription);
              break;
            case "Manual Restraint by SAFY Employee":
              report += getFieldValue(dataResult.RestraintDescription);
              break;
            case "Medication Refusal":
              report += getFieldValue(dataResult.MedicationRefusalDescription);
              break;
            case "Medication Error":
              report += getFieldValue(dataResult.MedicationErrorDescription);
              break;
            case "Psychiatric Emergency, excluding SI, HI or Self-Harm":
              report += getFieldValue(
                dataResult.PsychiatricEmergencyDescription
              );
              break;
            case "Injury or Illness requiring medical attention":
              report += getFieldValue(dataResult.MedicalAttentionDescription);
              break;
            case "Problematic Sexualized Behaviors":
              report += getFieldValue(
                dataResult.SexualizedBehaviorsDescription
              );
              break;
            case "Problematic Behaviors at School/Other Care Settings":
              report += getFieldValue(
                dataResult.ProblematicBehaviorsDescription
              );
              break;
            case "Self-Harm Behaviors":
              report += getFieldValue(dataResult.SelfHarmBehaviorsDescription);
              break;
            case "Suicidal Thoughts, Gestures, Threats":
              report += getFieldValue(dataResult.SuicidalThoughtsDescription);
              break;
            case "Suicide Attempt":
              report += getFieldValue(dataResult.SuicideAttemptDescription);
              break;
            case "Illegal Behavior (to include substance use)":
              report += getFieldValue(dataResult.IllegalBehaviorDescription);
              break;
            case "Aggression/Destruction towards people/property":
              report += getFieldValue(dataResult.AggressionDescription);
              break;
            case "Car Accident with SAFY Employee":
              report += getFieldValue(dataResult.CarAccidentDescription);
              break;
            case "Victim in an Unsafe Peer Interaction":
              report += getFieldValue(dataResult.VictimDescription);
              break;
            case "Other":
              report += getFieldValue(dataResult.OtherTypeDescription);
              break;
            default:
              break;
          }

          processedReports.push(report);
        });

      obj.incidents["@value"] = `${
        obj.incidents["@value"]
          ? `${obj.incidents["@value"]}

`
          : ""
      }${processedReports.join(`

`)}`;
      return obj;
    });
  }, [incidentReports]);

  const processSoarIncidentReports = useCallback(() => {
    setData((d) => {
      const obj = { ...d };
      const startValue = getFieldValue(obj.beginDate);
      const endValue = getFieldValue(obj.endDate);

      if (!startValue || !endValue) {
        return obj;
      }

      const startDate = moment(startValue);
      const endDate = moment(endValue);
      const monthlyReports = soarIncidentReports.filter((report) => {
        const incidentDateTime = moment(
          getFieldDate(report.ccx_dateandtimeofincident)
        );

        return (
          getFieldText(report.sor_clientprogram) ===
            getFieldValue(obj.SelectedProgram) &&
          incidentDateTime.isSameOrAfter(startDate) &&
          incidentDateTime.isSameOrBefore(endDate)
        );
      });

      obj.incidents["@value"] = monthlyReports.map((report) => {
        const incidentDateTime = moment(
          getFieldDate(report.ccx_dateandtimeofincident)
        );
        return `${incidentDateTime.format("MMM. Do")} - ${
          report.sor_soarincidenttype["@name"]
        }`;
      }).join(`
  `);

      return obj;
    });
  }, [soarIncidentReports]);

  const handleReportDateChange = (e) => {
    handleChange(e);
    processSoarIncidentReports();
    processFormsyIncidentReports();
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <FormInput
                    name="beginDate"
                    label={data.beginDate["@label"]}
                    error={errors.beginDate}
                    value={getFieldValue(data.beginDate)}
                    onChange={handleReportDateChange}
                    identical={compare("beginDate")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="date"
                  />
                </div>
                <div className="col">
                  <FormInput
                    name="endDate"
                    label={data.endDate["@label"]}
                    error={errors.endDate}
                    value={getFieldValue(data.endDate)}
                    onChange={handleReportDateChange}
                    identical={compare("endDate")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="date"
                  />
                </div>
              </div>
              <FormTextArea
                name="progress"
                label={data.progress["@label"]}
                error={errors.progress}
                value={getFieldValue(data.progress)}
                onChange={handleChange}
                identical={compare("progress")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                rows="5"
              />
              <FormTextArea
                name="incidents"
                label={data.incidents["@label"]}
                error={errors.incidents}
                value={getFieldValue(data.incidents)}
                onChange={handleChange}
                identical={compare("incidents")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                rows="3"
              />
              <FormRadioButton
                name="educationalProgressChange"
                label={data.educationalProgressChange["@label"]}
                error={errors.educationalProgressChange}
                value={getFieldValue(data.educationalProgressChange)}
                options={JSON.parse(
                  data.educationalProgressChange["@options"].replaceAll(
                    "'",
                    '"'
                  )
                )}
                onChange={handleChange}
                identical={compare("educationalProgress")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormTextArea
                name="educationalProgress"
                label={data.educationalProgress["@label"]}
                error={errors.educationalProgress}
                value={getFieldValue(data.educationalProgress)}
                onChange={handleChange}
                identical={compare("educationalProgress")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                rows="3"
              />
              <FormTextArea
                name="functioning"
                label={data.functioning["@label"]}
                error={errors.functioning}
                value={getFieldValue(data.functioning)}
                onChange={handleChange}
                identical={compare("functioning")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                rows="3"
              />
              <FormRadioButton
                name="physicalConditionChange"
                label={data.physicalConditionChange["@label"]}
                error={errors.physicalConditionChange}
                value={getFieldValue(data.physicalConditionChange)}
                options={JSON.parse(
                  data.physicalConditionChange["@options"].replaceAll("'", '"')
                )}
                onChange={handleChange}
                identical={compare("physicalConditionChange")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormTextArea
                name="physicalCondition"
                label={data.physicalCondition["@label"]}
                error={errors.physicalCondition}
                value={getFieldValue(data.physicalCondition)}
                onChange={handleChange}
                identical={compare("physicalCondition")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                rows="3"
              />
              <FormRadioButton
                name="familySituationChange"
                label={data.familySituationChange["@label"]}
                error={errors.familySituationChange}
                value={getFieldValue(data.familySituationChange)}
                options={JSON.parse(
                  data.familySituationChange["@options"].replaceAll("'", '"')
                )}
                onChange={handleChange}
                identical={compare("familySituationChange")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormTextArea
                name="familySituation"
                label={data.familySituation["@label"]}
                error={errors.familySituation}
                value={getFieldValue(data.familySituation)}
                onChange={handleChange}
                identical={compare("familySituation")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                rows="3"
              />
              <FormRadioButton
                name="primaryContactChange"
                label={data.primaryContactChange["@label"]}
                error={errors.primaryContactChange}
                value={getFieldValue(data.primaryContactChange)}
                options={JSON.parse(
                  data.primaryContactChange["@options"].replaceAll("'", '"')
                )}
                onChange={handleChange}
                identical={compare("primaryContactChange")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormTextArea
                name="primaryContact"
                label={data.primaryContact["@label"]}
                error={errors.primaryContact}
                value={getFieldValue(data.primaryContact)}
                onChange={handleChange}
                identical={compare("primaryContact")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                rows="3"
              />
              <div className="h4 mt-4 mb-2">
                {data.significantEvents["@label"]}
              </div>
              <div className="mb-2">{data.checkIf["@label"]}</div>
              <FormCheckbox
                name="clientRanAway"
                label={data.clientRanAway["@label"]}
                checked={getFieldValue(data.clientRanAway) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <FormCheckbox
                name="experiencedSIHI"
                label={data.experiencedSIHI["@label"]}
                checked={getFieldValue(data.experiencedSIHI) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <FormCheckbox
                name="placementChanged"
                label={data.placementChanged["@label"]}
                checked={getFieldValue(data.placementChanged) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <FormCheckbox
                name="clientDischarged"
                label={data.clientDischarged["@label"]}
                checked={getFieldValue(data.clientDischarged) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3"
              />
              <FormCheckbox
                name="other"
                label={data.other["@label"]}
                checked={getFieldValue(data.other) === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
                divClassName="ms-3 mb-2"
              />
              <FormTextArea
                name="otherText"
                label={data.otherText["@label"]}
                error={errors.otherText}
                value={getFieldValue(data.otherText)}
                onChange={handleChange}
                identical={compare("otherText")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                rows="3"
                divClassName="mb-2"
              />
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={helperConfiguration.finalSignType}
              initialSignatureType={helperConfiguration.signatureType}
            />
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default COMonthlyReport;
